import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Upload, Button, Typography, App } from 'antd';
import { InboxOutlined, DeleteOutlined } from '@ant-design/icons';
import { collection, addDoc, serverTimestamp, updateDoc, doc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db } from '../data/base';
import { secureGetItem } from '../data/utils';
import MediumModal from './Components/Modals/MediumModal';

const { TextArea } = Input;
const { Text } = Typography;
const { Option } = Select;

const SupportModal = ({ visible, onClose }) => {
    const { message } = App.useApp();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [uploadedImages, setUploadedImages] = useState([]);
    const [uploading, setUploading] = useState(false);

    // Ottieni l'email dell'utente corrente
    const userEmail = secureGetItem('email');

    // Reset del form quando la modale si apre
    useEffect(() => {
        if (visible) {
            form.setFieldsValue({
                supportType: undefined,
                title: '',
                description: ''
            });
            setUploadedImages([]);
        }
    }, [visible, form, userEmail]);

    // Funzione per comprimere le immagini
    const compressImage = (file, quality = 0.8) => {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const scale = 0.8; // Riduci le dimensioni dell'80%
                    canvas.width = img.width * scale;
                    canvas.height = img.height * scale;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                    canvas.toBlob((blob) => {
                        const compressedFile = new File(
                            [blob],
                            file.name.replace(/\.\w+$/, '.jpg'),
                            { type: 'image/jpeg' }
                        );
                        resolve(compressedFile);
                    }, 'image/jpeg', quality);
                };
                img.src = e.target.result;
            };
            reader.readAsDataURL(file);
        });
    };

    // Gestione upload immagini
    const handleImageUpload = async (file) => {
        try {
            setUploading(true);

            // Check maximum number of images
            if (uploadedImages.length >= 5) {
                message.warning('You can upload maximum 5 images');
                return false;
            }

            // Check file type
            if (!file.type.startsWith('image/')) {
                message.error('You can only upload image files');
                return false;
            }

            // Check file size (max 10MB)
            if (file.size > 10 * 1024 * 1024) {
                message.error('Images cannot exceed 10MB');
                return false;
            }

            // Compress the image
            const compressedFile = await compressImage(file);

            // Create storage path based on current date
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');

            // Store temporarily using a unique filename until we have the document ID
            const storage = getStorage();
            const tempFileName = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}_${compressedFile.name}`;
            const tempStoragePath = `SupportRequests/temp/${tempFileName}`;
            const storageRef = ref(storage, tempStoragePath);

            // Upload the file
            await uploadBytes(storageRef, compressedFile);
            const downloadURL = await getDownloadURL(storageRef);

            // Add the image to the array with additional information
            setUploadedImages(prev => [...prev, {
                url: downloadURL,
                originalName: file.name,
                tempStoragePath: tempStoragePath,
                tempFileName: tempFileName
            }]);

            message.success('Image uploaded successfully');
            return false; // Prevent default upload behavior
        } catch (error) {
            console.error('Error uploading image:', error);
            message.error('Error uploading image');
        } finally {
            setUploading(false);
        }
    };

    // Remove image
    const removeImage = (index) => {
        setUploadedImages(prev => prev.filter((_, i) => i !== index));
    };

    // Function to move images to the correct folder after creating the document
    const moveImagesToCorrectFolder = async (documentId, tempImages) => {
        // console.log('Moving images for document:', documentId);
        // console.log('Images to move:', tempImages);

        const storage = getStorage();
        const movedImages = [];

        for (const image of tempImages) {
            try {
                const now = new Date();
                const year = now.getFullYear();
                const month = String(now.getMonth() + 1).padStart(2, '0');
                const day = String(now.getDate()).padStart(2, '0');

                const finalFileName = `${Date.now()}_${image.originalName}`;
                const newStoragePath = `SupportRequests/${year}/${month}/${day}/${documentId}/${finalFileName}`;
                const newStorageRef = ref(storage, newStoragePath);

                // console.log('Moving image from:', image.tempStoragePath);
                // console.log('Moving image to:', newStoragePath);

                // Download the file from temporary location
                const response = await fetch(image.url);
                const blob = await response.blob();

                // Upload to new location
                await uploadBytes(newStorageRef, blob);
                const newDownloadURL = await getDownloadURL(newStorageRef);

                const movedImage = {
                    url: newDownloadURL,
                    originalName: image.originalName,
                    storagePath: newStoragePath
                };

                movedImages.push(movedImage);
                // console.log('Successfully moved image:', movedImage);

                // Delete the temporary file
                try {
                    const tempRef = ref(storage, image.tempStoragePath);
                    await deleteObject(tempRef);
                    // console.log('Successfully deleted temporary file:', image.tempStoragePath);
                } catch (deleteError) {
                    console.error('Error deleting temporary file:', deleteError);
                    // Non-critical error, continue with the process
                }
            } catch (error) {
                console.error('Error moving image:', error);
                // In case of error, keep the original URL
                const fallbackImage = {
                    url: image.url,
                    originalName: image.originalName,
                    storagePath: image.tempStoragePath
                };
                movedImages.push(fallbackImage);
                // console.log('Using fallback image:', fallbackImage);
            }
        }

        // console.log('Final moved images:', movedImages);
        return movedImages;
    };

    // Funzione per chiamare l'API di notifica agli admin
    const notifyAdmins = async (supportRequestId) => {
        try {
            const apiUrl = process.env.REACT_APP_SUPPORT_REQUESTS;

            if (!apiUrl) {
                console.error('API URL for support requests not configured');
                return;
            }

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    supportRequestId: supportRequestId
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            // console.log('Admin notification sent successfully:', result);
        } catch (error) {
            console.error('Error notifying admins:', error);
            // Non blocchiamo il processo anche se la notifica fallisce
        }
    };

    // Handle form submission
    const handleSubmit = async () => {
        try {
            setLoading(true);

            const values = await form.validateFields();

            // First, create the document to get the ID
            const supportRequestData = {
                email: userEmail.toLowerCase(),
                supportType: values.supportType,
                title: values.title,
                description: values.description,
                images: uploadedImages.map(img => ({
                    url: img.url,
                    originalName: img.originalName,
                    storagePath: img.tempStoragePath
                })), // Use current uploaded images
                createdAt: serverTimestamp()
            };

            // Save the document to Firestore
            const docRef = await addDoc(collection(db, 'SupportRequests'), supportRequestData);
            const documentId = docRef.id;

            // Move images to the correct folder if present
            if (uploadedImages.length > 0) {
                const finalImages = await moveImagesToCorrectFolder(documentId, uploadedImages);

                // Update the document with the moved images URLs
                await updateDoc(doc(db, 'SupportRequests', documentId), {
                    images: finalImages
                });
            }

            // Invia notifica agli admin
            await notifyAdmins(documentId);

            message.success('Support request sent successfully!');

            // Reset form and close modal
            form.resetFields();
            setUploadedImages([]);
            onClose();

        } catch (error) {
            console.error('Error sending support request:', error);
            message.error('Error sending request. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <MediumModal
            visible={visible}
            onClose={onClose}
            title=""
            onConfirm={handleSubmit}
            confirmText="Send Request"
            cancelText="Cancel"
            centered={true}
        >
            <div style={{
                marginBottom: '1.5rem',
                textAlign: 'center'
            }}>
                <Text style={{
                    fontSize: '1rem',
                    color: '#000',
                    lineHeight: '1.6',
                    fontWeight: 'bold'
                }}>
                    Need a hand?
                </Text>
                <br />
                <br />
                <Text style={{
                    fontSize: '1rem',
                    color: '#000',
                    lineHeight: '1.6'
                }}>
                    Whether you've encountered a bug, require technical assistance or simply have a question, our team is here to help. Please fill in the form below and we'll get back to you within 24 hours.
                </Text>
            </div>

            <Form
                form={form}
                layout="vertical"
                requiredMark={false}
                style={{ maxWidth: '100%' }}
            >
                <Form.Item
                    label="Support Type"
                    name="supportType"
                    rules={[{ required: true, message: 'Please select the support type' }]}
                >
                    <Select placeholder="Select the support type">
                        <Option value="Customer Support">Customer Support</Option>
                        <Option value="Bug Report">Bug Report</Option>
                        <Option value="Technical Issue">Technical Issue</Option>
                        <Option value="Other">Other</Option>
                    </Select>
                </Form.Item>

                <Form.Item
                    label="Title"
                    name="title"
                    rules={[
                        { required: true, message: 'Please enter a title for your request' },
                        { max: 100, message: 'Title cannot exceed 100 characters' }
                    ]}
                >
                    <Input placeholder="Enter a title for your request" />
                </Form.Item>

                <Form.Item
                    label="Description"
                    name="description"
                    rules={[
                        { required: true, message: 'Please enter a detailed description' },
                        { min: 10, message: 'Description must contain at least 10 characters' }
                    ]}
                >
                    <TextArea
                        rows={4}
                        placeholder="Describe the problem or request in detail..."
                        maxLength={1000}
                        showCount
                    />
                </Form.Item>

                <Form.Item label="Attachments (max 5 images)">
                    <Upload.Dragger
                        multiple
                        accept="image/*"
                        beforeUpload={handleImageUpload}
                        showUploadList={false}
                        disabled={uploading || uploadedImages.length >= 5}
                    >
                        <p className="ant-upload-drag-icon">
                            <InboxOutlined />
                        </p>
                        <p className="ant-upload-text">
                            {uploadedImages.length >= 5
                                ? 'Maximum number of images reached'
                                : 'Click or drag files here to upload'
                            }
                        </p>
                        <p className="ant-upload-hint">
                            Only image files are supported (max 10MB each)
                        </p>
                    </Upload.Dragger>

                    {/* Preview of uploaded images */}
                    {uploadedImages.length > 0 && (
                        <div style={{ marginTop: 16 }}>
                            <Text strong>Uploaded images ({uploadedImages.length}/5):</Text>
                            <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 8,
                                marginTop: 8
                            }}>
                                {uploadedImages.map((image, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            position: 'relative',
                                            width: 100,
                                            height: 100,
                                            border: '1px solid #d9d9d9',
                                            borderRadius: 6,
                                            overflow: 'hidden'
                                        }}
                                    >
                                        <img
                                            src={image.url}
                                            alt={image.originalName}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover'
                                            }}
                                        />
                                        <Button
                                            type="text"
                                            danger
                                            icon={<DeleteOutlined />}
                                            size="small"
                                            onClick={() => removeImage(index)}
                                            style={{
                                                position: 'absolute',
                                                top: 4,
                                                right: 4,
                                                minWidth: 24,
                                                height: 24,
                                                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                                borderRadius: '50%'
                                            }}
                                        />
                                        <div
                                            style={{
                                                position: 'absolute',
                                                bottom: 0,
                                                left: 0,
                                                right: 0,
                                                background: 'rgba(0, 0, 0, 0.7)',
                                                color: 'white',
                                                fontSize: 10,
                                                padding: 2,
                                                textAlign: 'center',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                            title={image.originalName}
                                        >
                                            {image.originalName}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </Form.Item>
            </Form>
        </MediumModal>
    );
};

export default SupportModal; 